import { useCallback, useRef } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalProps,
  useToast,
  Text,
  FormControl,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputField } from '@/components/InputField';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { ModalFormData, modalFormSchema } from './schemas';
import { TeamTimeline } from '@/model';
import { usePutTimelineMutation } from '@/services/queries/team-timeline';
import { PutTeamTeamTimelineRequest } from '@/services/request/team-timeline';
import { TextareaField } from '@/components/TextareaField';
import { useTranslation } from 'react-i18next';
import ControlTeamFormSelect from '@/components/ControlTeamFormSelect';

export interface TeamTimelineEditModalProps extends Pick<ModalProps, 'isOpen' | 'onClose'> {
  isOpen: boolean;
  item: TeamTimeline;
  onClose: () => void;
  onDone?: () => void;
}

export const TeamTimelineEditModal = ({ isOpen, onClose, item, onDone }: TeamTimelineEditModalProps) => {
  const toast = useToast();
  const putItemMutation = usePutTimelineMutation();
  const { t } = useTranslation();

  const {
    handleSubmit,
    register,
    formState: { errors },
    clearErrors,
    reset,
    control,
  } = useForm<ModalFormData>({
    defaultValues: {
      team_uuid: item.team_uuid,
      title: item.title,
      description: item.description,
      year: item.year,
    },
    resolver: zodResolver(modalFormSchema),
  });

  const formRef = useRef<HTMLFormElement>(null);

  const handleConfirm = useCallback(() => {
    formRef?.current?.requestSubmit();
  }, []);

  const handleClose = useCallback(() => {
    clearErrors();
    onClose?.();
    reset();
  }, [clearErrors, onClose, reset]);

  const onSubmit = useCallback(
    (data: ModalFormData) => {
      if (!item) {
        return;
      }

      const payload: PutTeamTeamTimelineRequest = {
        team_uuid: data?.team_uuid,
        title: data.title,
        description: data.description,
        year: data.year,
      };

      putItemMutation.mutate(
        {
          id: item.uuid,
          payload,
        },
        {
          onSuccess: () => {
            onDone?.();
            toast({
              status: 'success',
              title: t('pages.application_history_timeline.components.team_timeline_edit_modal.team_timeline_edit_success'),
            });
          },
          onError: (resp: any) => {
            toast({
              status: 'error',
              title:
                (resp && resp.message) ||
                t('pages.application_history_timeline.components.team_timeline_edit_modal.team_timeline_edit_error'),
            });
          },
        },
      );
    },
    [item, onDone, putItemMutation, toast, t],
  );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Flex w='100%' justifyContent='space-between' alignItems='center' pb={2} borderBottom='2px solid' borderColor='black'>
            <Text fontSize='medium' fontWeight='bold'>
              {t('pages.application_history_timeline.components.team_timeline_edit_modal.team_timeline_edit_title')}
            </Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex ref={formRef} as='form' onSubmit={handleSubmit(onSubmit)} direction='column' gap={6}>
            <ControlTeamFormSelect<ModalFormData, 'team_uuid'>
              control={control}
              name='team_uuid'
              prefetchOnOpen
              label={t('pages.application_history_timeline.components.team_timeline_edit_modal.team_timeline_edit_team_label')}
              placeholder={t(
                'pages.application_history_timeline.components.team_timeline_edit_modal.team_timeline_edit_team_label',
              )}
              showFlag
              width='100%'
              usePortal={false}
              error={errors?.team_uuid?.message as string | undefined}
            />
            <Flex direction='row' gap={6} width='100%'>
              <InputField
                withoutFloating
                label={t('pages.application_history_timeline.components.team_timeline_edit_modal.team_timeline_edit_year_label')}
                type='number'
                {...register('year', {
                  valueAsNumber: true,
                })}
                maxLength={4}
                rootProps={{ isInvalid: !!errors?.year, errorMessage: errors?.year?.message }}
                flex='1'
              />

              <InputField
                withoutFloating
                label={t('pages.application_history_timeline.components.team_timeline_edit_modal.team_timeline_edit_title_label')}
                {...register('title')}
                helperText={t(
                  'pages.application_history_timeline.components.team_timeline_edit_modal.team_timeline_edit_title_helper',
                )}
                maxLength={50}
                rootProps={{ isInvalid: !!errors?.title, errorMessage: errors?.title?.message }}
                flex='1'
              />
            </Flex>

            <FormControl display='flex' flexDirection='column' gap={2} isInvalid={!!errors?.description}>
              <Text fontWeight='medium'>
                {t('pages.application_history_timeline.components.team_timeline_edit_modal.team_timeline_edit_description_label')}
              </Text>
              <TextareaField
                maxLength={200}
                helperText={t(
                  'pages.application_history_timeline.components.team_timeline_edit_modal.team_timeline_edit_description_helper',
                )}
                error={errors?.description?.message}
                {...register('description')}
              />
            </FormControl>
          </Flex>
        </ModalBody>

        <ModalFooter justifyContent={'space-between'} gap='kilo'>
          <Flex w={'100%'} justifyContent='end' gap={5}>
            <Button variant='outlined' color='gray.600' onClick={onClose}>
              {t('pages.application_history_timeline.components.team_timeline_edit_modal.team_timeline_edit_cancel_button')}
            </Button>
            <Button onClick={handleConfirm}>
              {t('pages.application_history_timeline.components.team_timeline_edit_modal.team_timeline_edit_save_button')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
      <CenteredSpinner isLoading={putItemMutation.isLoading} />
    </Modal>
  );
};
